import type { FC } from 'react';
import React, { useCallback, useMemo } from 'react';
import type { CreateRecipientRequest } from '@pflegenavi/service-provider-frontend/api';
import {
  useCreateRecipient,
  useDeactivateRecipient,
  useGetAllCountriesPhoenix,
  useGetServiceProviderPhoenix,
  useSortedCountries,
} from '@pflegenavi/service-provider-frontend/api';
import { useNavigate } from 'react-router-dom';
import { spAppMainPages } from '@pflegenavi/frontend/routing';
import { useAuthentication } from '@pflegenavi/frontend/authentication';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import {
  Button,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { WelcomeSideBar } from '../components/general/WelcomeSideBar';
import { MangopayRecipientForm } from '../components/connect-account/MangopayRecipientForm';
import { useErrorSnackbar } from '@pflegenavi/web-components';
import { keepPreviousData } from '@tanstack/react-query';
import { useBankAccountStatus } from '../hooks/useBankAccountStatus';

export const SetupBankAccountPage: FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { enqueueErrorSnackbar } = useErrorSnackbar();
  const { t } = useTranslation();

  const { user } = useAuthentication();
  const userId = user?.userId;

  const {
    data: serviceProviderProfile,
    isLoading: isServiceProviderProfileLoading,
  } = useGetServiceProviderPhoenix();

  const { mutateAsync: createRecipient, isPending: isCreateRecipientLoading } =
    useCreateRecipient();

  const { mutateAsync: deactivateRecipient, isPending: isDeactivating } =
    useDeactivateRecipient();

  // MangoPay only lets a CANCELED/DEACTIVATED recipient be replaced, and only an ACTIVE recipient
  // can be deactivated. A PENDING recipient (SCA never confirmed) can neither be deactivated nor
  // replaced - the SP has to finish SCA or wait for it to expire.
  const {
    isReady: hasActiveRecipient,
    recipientPending,
    scaUrl,
    openScaUrl,
  } = useBankAccountStatus();

  const handleDeactivateRecipient = useCallback(async () => {
    try {
      await deactivateRecipient();
      enqueueSnackbar(
        t('service-provider-app.connect-account.recipient-deactivated'),
        { variant: 'success' }
      );
    } catch (error) {
      enqueueErrorSnackbar(
        error,
        t('service-provider-app.connect-account.failed-to-deactivate-recipient')
      );
    }
  }, [deactivateRecipient, enqueueSnackbar, enqueueErrorSnackbar, t]);

  const { data: countriesData, isPending: areCountriesLoading } =
    useGetAllCountriesPhoenix({
      refetchOnWindowFocus: false,
      placeholderData: keepPreviousData,
    });

  const countries = useSortedCountries(countriesData ?? []);

  const handleCreateRecipient = useCallback(
    async (request: CreateRecipientRequest) => {
      try {
        if (!userId) {
          throw new Error('Missing user id');
        }

        const response = await createRecipient(request);

        // If we have a redirect URL for SCA, return it
        if (response.data.redirect_url) {
          // Add returnUrl for proper navigation after SCA
          const returnUrl = `${window.location.origin}${spAppMainPages.DASHBOARD}`;

          // Check if the redirect URL already has query parameters
          const separator = response.data.redirect_url.includes('?')
            ? '&'
            : '?';
          const redirectUrl = `${
            response.data.redirect_url
          }${separator}returnUrl=${encodeURIComponent(returnUrl)}`;

          return {
            data: {
              redirect_url: redirectUrl,
              service_provider: response.data.service_provider,
            },
          };
        }

        // Success - show message and navigate
        enqueueSnackbar(
          t('service-provider-app.connect-account.recipient-created'),
          { variant: 'success' }
        );

        navigate(spAppMainPages.DASHBOARD);
        return { data: { service_provider: response.data.service_provider } };
      } catch (error) {
        enqueueErrorSnackbar(
          error,
          t('service-provider-app.connect-account.failed-to-create-recipient')
        );
        throw error;
      }
    },
    [
      userId,
      createRecipient,
      enqueueSnackbar,
      navigate,
      t,
      enqueueErrorSnackbar,
    ]
  );

  const handleSkipBankAccount = useCallback(() => {
    enqueueSnackbar(
      t('service-provider-app.connect-account.bank-account-skipped'),
      { variant: 'info' }
    );
    navigate(spAppMainPages.DASHBOARD);
    return Promise.resolve();
  }, [enqueueSnackbar, navigate, t]);

  const loading = useMemo(() => {
    return (
      areCountriesLoading ||
      isCreateRecipientLoading ||
      isServiceProviderProfileLoading
    );
  }, [
    areCountriesLoading,
    isCreateRecipientLoading,
    isServiceProviderProfileLoading,
  ]);

  if (areCountriesLoading || isServiceProviderProfileLoading) {
    return (
      <Stack
        sx={{
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          overflow: 'hidden',
          width: '100%',
          display: 'flex',
        }}
      >
        <CircularProgress color="primary" variant="indeterminate" />
      </Stack>
    );
  }

  return (
    <Grid
      container
      sx={{
        height: '100vh',
        overflow: 'hidden',
      }}
    >
      <Grid
        size={{
          md: 3,
        }}
        sx={{
          height: '100vh',
        }}
      >
        <WelcomeSideBar
          title={t('service-provider-app.connect-account.hello')}
          subtitle={t(
            'service-provider-app.connect-account.welcome-to-pflegenavi'
          )}
        />
      </Grid>
      <Grid
        size={{
          md: 9,
        }}
        sx={{
          height: '100vh',
          overflow: 'auto',
          position: 'relative',
          pb: 10,
        }}
      >
        {hasActiveRecipient ? (
          <Stack spacing={2} sx={{ p: 4, maxWidth: 640, mx: 'auto' }}>
            <Typography variant="h5">
              {t(
                'service-provider-app.connect-account.replace-bank-account-title'
              )}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
              }}
            >
              {t(
                'service-provider-app.connect-account.replace-bank-account-description'
              )}
            </Typography>
            <Button
              variant="contained"
              color="error"
              onClick={handleDeactivateRecipient}
              disabled={isDeactivating}
            >
              {t(
                'service-provider-app.connect-account.replace-bank-account-confirm'
              )}
            </Button>
          </Stack>
        ) : recipientPending ? (
          <Stack spacing={2} sx={{ p: 4, maxWidth: 640, mx: 'auto' }}>
            <Typography variant="h5">
              {t('service-provider-app.profile.verification-pending')}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
              }}
            >
              {t(
                'service-provider-app.profile.bank-account-verification-pending-description'
              )}
            </Typography>
            {scaUrl ? (
              <Button variant="contained" onClick={openScaUrl}>
                {t(
                  'service-provider-app.dashboard.resume-bank-account-verification-button'
                )}
              </Button>
            ) : (
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {t(
                  'service-provider-app.connect-account.pending-recipient-expires'
                )}
              </Typography>
            )}
          </Stack>
        ) : (
          <MangopayRecipientForm
            loading={loading}
            countryOptions={countries ?? []}
            handleCreateRecipient={handleCreateRecipient}
            handleSkipBankAccount={handleSkipBankAccount}
            legalPersonType={serviceProviderProfile?.data.legal_person_type}
            serviceProviderCountryId={
              serviceProviderProfile?.data.country_id ?? undefined
            }
          />
        )}
      </Grid>
    </Grid>
  );
};
